import React, { useState, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Tag,
  Statistic,
  Button,
  Icon,
  Descriptions,
  Checkbox,
} from "antd";
import AnimateHeight from "react-animate-height";
import ParcelasTabelaSimplificada from "../ParcelasTabelaSimplificada";
import { ParticipantesModal } from "../Contrato/ParticipantesModal";
import * as MoneyFormater from "../../../../utils/MoneyFormater";
import * as DateFormater from "../../../../utils/DateFormater";
import "./styles.css";

const ContratoView = ({ operacao, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [ocultarInativos, setOcultarInativos] = useState(false);
  const [showParticipantes, setShowParticipantes] = useState(false);

  // Calcular estatísticas
  const stats = useMemo(() => {
    const parcelas = operacao.parcela_set || [];
    const parcelasAtivas = parcelas.filter((p) => !p.is_deleted);

    const somaParcelas = parcelas.reduce(
      (sum, p) => sum + (parseFloat(p.valor_parcela) || 0),
      0
    );
    const somaAtualizada = parcelas.reduce(
      (sum, p) => sum + (parseFloat(p.valor_atualizado) || 0),
      0
    );
    const somaVencidas = parcelas
      .filter((p) => {
        if (!p.dt_vencimento || p.is_deleted) return false;
        const hoje = new Date();
        const vencimento = new Date(p.dt_vencimento);
        return vencimento < hoje && p.situacao !== "PAGO";
      })
      .reduce((sum, p) => sum + (parseFloat(p.valor_atualizado) || 0), 0);

    return {
      parcelasAtivas: parcelasAtivas.length,
      somaParcelas,
      somaAtualizada,
      somaVencidas,
    };
  }, [operacao]);

  const parcelas = useMemo(() => {
    if (!operacao.parcela_set) return [];
    return ocultarInativos
      ? operacao.parcela_set.filter((p) => !p.is_deleted)
      : operacao.parcela_set;
  }, [operacao.parcela_set, ocultarInativos]);

  return (
    <div>
      <Card className="contrato__card">
        {/* HEADER - Sempre visível */}
        <div className="contrato__header">
          <Row gutter={16} align="middle">
            <Col xs={24} md={8}>
              <div className="contrato__basic-info">
                <div className="contrato__title-line">
                  <h3 className="contrato__numero">
                    {operacao.id_numero_contrato}
                  </h3>
                  <Tag
                    className="contrato__status-tag"
                    color={operacao.is_deleted ? "red" : "green"}
                  >
                    {operacao.is_deleted ? "INATIVO" : "ATIVO"}
                  </Tag>
                </div>

                <div className="contrato__tags">
                  <Tag color="blue">{operacao.produto}</Tag>
                  <Tag>{operacao.carteira}</Tag>
                </div>
              </div>
            </Col>

            <Col xs={24} md={12}>
              <Row gutter={8}>
                <Col span={6}>
                  <Statistic
                    title="Valor"
                    value={operacao.valor || 0}
                    prefix="R$"
                    precision={2}
                    valueStyle={{ fontSize: 16 }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Valor Contábil"
                    value={operacao.valor_contabil || 0}
                    prefix="R$"
                    precision={2}
                    valueStyle={{ fontSize: 16 }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Parcelas"
                    value={operacao.numero_parcelas || 0}
                    valueStyle={{ fontSize: 16 }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Parcelas Ativas"
                    value={stats.parcelasAtivas}
                    valueStyle={{ color: "#3f8600", fontSize: 16 }}
                  />
                </Col>
              </Row>
            </Col>

            <Col xs={24} md={4} style={{ textAlign: "right" }}>
              <Button onClick={() => setExpanded(!expanded)}>
                {expanded ? "Ocultar" : "Detalhes"}
                <Icon type={expanded ? "up" : "down"} />
              </Button>
            </Col>
          </Row>
        </div>

        {/* ÁREA EXPANSÍVEL */}
        <AnimateHeight duration={350} height={expanded ? "auto" : 0}>
          <div className="contrato__content">
            {/* Cards de Informações */}
            <Row gutter={16} className="mb-3">
              <Col xs={24} md={8}>
                <Card
                  size="small"
                  title="Informações Gerais"
                  style={{ borderRadius: "5px" }}
                >
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Responsabilidade">
                      {operacao.responsabilidade || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Data Contratação">
                      {operacao.dt_contratacao
                        ? DateFormater.format(operacao.dt_contratacao)
                        : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Data Cadastro">
                      {operacao.dt_cadastro
                        ? DateFormater.format(operacao.dt_cadastro)
                        : "-"}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>

              <Col xs={24} md={8}>
                <Card
                  size="small"
                  title="Valores Financeiros"
                  style={{ borderRadius: "5px" }}
                >
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Soma Parcelas">
                      R$ {MoneyFormater.format(stats.somaParcelas)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Soma Atualizada">
                      R$ {MoneyFormater.format(stats.somaAtualizada)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Soma Vencidas">
                      R$ {MoneyFormater.format(stats.somaVencidas)}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>

              <Col xs={24} md={8}>
                <Card
                  size="small"
                  title="Ações"
                  style={{ borderRadius: "5px" }}
                >
                  <Button
                    icon="team"
                    onClick={() => setShowParticipantes(true)}
                    block
                    style={{ marginBottom: 8 }}
                  >
                    Ver Participantes (
                    {(operacao.participante_set &&
                      operacao.participante_set.length) ||
                      0}
                    )
                  </Button>
                </Card>
              </Col>
            </Row>

            {/* Filtros */}
            <div className="contrato__filters">
              <Checkbox
                checked={ocultarInativos}
                onChange={(e) => setOcultarInativos(e.target.checked)}
              >
                Ocultar parcelas inativas
              </Checkbox>
            </div>

            {/* Tabela de Parcelas Simplificada */}
            <ParcelasTabelaSimplificada
              parcelas={parcelas}
              operacaoId={operacao.id}
              onUpdate={onUpdate}
            />
          </div>
        </AnimateHeight>
      </Card>

      {/* Modal de Participantes */}
      <ParticipantesModal
        visible={showParticipantes}
        onCancel={() => setShowParticipantes(false)}
        participantes={operacao.participante_set || []}
      />
    </div>
  );
};

export default ContratoView;



// WEBPACK FOOTER //
// ./src/pages/Pasta/Contratos/ContratoView/index.jsx